/* @flow */
import { graphql } from 'react-apollo';
import { partition, xor, isEmpty } from 'lodash';
import * as React from 'react';
import ContentLoader from 'react-content-loader';
import cx from 'classnames';
import idx from 'idx';

import { isAdmin } from 'shapes/Affiliation';
import { roleNameSingular } from 'utils/roleHelpers';
import AdminResignModal from 'admin/components/AdminResignModal';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Coin from 'components/Coin';
import CircleSpinner from 'components/CircleSpinner';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph';
import GroupAvatar from 'groups/components/GroupAvatar';
import Image from 'components/Image';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import OrgRoleDropdownMenu from 'groups/components/OrgRoleDropdownMenu';
import SettingsRemoveSchoolModal from './SettingsRemoveSchoolModal';
import Text from 'components/Text';
import SETTINGS_SCHOOLS_LIST_ITEM_QUERY from '../queries/SettingsSchoolsListItemQuery.gql';
import type {
  SettingsSchoolsListItem as SettingsSchoolsListItemData,
  OrganizationRole,
} from '../queries/__generated__/SettingsSchoolsListItem';

import './styles/SettingsSchoolsListItem.scss';

import { AvatarImages } from 'constants/AvatarConstants';
import TooltipTrigger from '../../components/TooltipTrigger';

const AVATAR_NUMBER = 3;

type Props = {
  affiliation: ?Object,
  school: *,
  groups: *,
  submitting: ?boolean,
  data: ReactApolloData<SettingsSchoolsListItemData>,
};

type State = {
  open: boolean,
  hovered: boolean,
  savingRole: boolean,
  roles: OrganizationRole[],
};

// prettier-ignore
const Ghost = () => (
  <ContentLoader
    height={42}
    width={500}
    speed={1}
    primaryColor={"#f2f3f5"}
    secondaryColor={"#ffffff"}
  >
    <rect x="48" y="15" rx="4" ry="4" width="300" height="10" />
    <circle cx="21.730023818941984" cy="21.670023818941985" r="14" />
  </ContentLoader>
)

class SettingsSchoolsListItem extends React.Component<Props, State> {
  static defaultProps = {
    submitting: false,
    groups: [],
  };

  state: State = {
    open: false,
    hovered: false,
    savingRole: false,
    roles: this.props.affiliation ? this.props.affiliation.roles : [],
  };

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.affiliation || !this.props.affiliation) {
      return;
    }

    if (prevProps.affiliation.roles.length !== this.props.affiliation.roles.length) {
      this.updateRoles(this.props.affiliation.roles);
    }
  }

  updateRoles = (roles: OrganizationRole[]) => {
    this.setState({ roles });
  };

  renderCannotLeave = (reasons: $ReadOnlyArray<string>) => {
    let cannotLeaveOrgText = 'You cannot leave this org';
    reasons.forEach(reason => {
      cannotLeaveOrgText += ': ' + reason + '\n';
    });
    return (
      <TooltipTrigger tooltip={cannotLeaveOrgText}>
        <span>
          <Button color="apple" data-test="cant-leave-org" disabled level="secondary" size="xsmall">
            Leave
          </Button>
        </span>
      </TooltipTrigger>
    );
  };

  renderCanLeave = (removeSchoolModal: React.Element<*>, disabled: ?boolean) => {
    return (
      <ModalTrigger className="SettingsSchoolsListItem-leaveButton" modal={removeSchoolModal}>
        <Button color="apple" disabled={disabled} level="secondary" size="xsmall">
          Leave
        </Button>
      </ModalTrigger>
    );
  };

  renderLeaveAction = (
    cannotLeaveOrgReasons: $ReadOnlyArray<string>,
    removeSchoolModal: React.Element<*>,
    disabled: ?boolean
  ) => {
    return cannotLeaveOrgReasons.length === 0
      ? this.renderCanLeave(removeSchoolModal, disabled)
      : this.renderCannotLeave(cannotLeaveOrgReasons);
  };

  renderHoverContent() {
    const { groups, affiliation, school, data } = this.props;
    const isParentOrgAdmin = idx(data.organizations[0], _ => _.isParentOrgAdmin);
    let _cannotLeaveOrgReasons: $ReadOnlyArray<string> = [];

    if (data.organizations[0]) {
      _cannotLeaveOrgReasons = data.organizations[0].permissions.cannotLeaveOrgReasons;
    }

    const groupsByOwned = partition(groups, group =>
      group.owners.find(user => user.id === CurrentUser.getUserId())
    );

    const removeSchoolModal = (
      <SettingsRemoveSchoolModal
        affiliation={affiliation}
        ownedGroups={groupsByOwned[0]}
        subscribedGroups={groupsByOwned[1]}
        schoolId={school.id}
        schoolName={school.name}
      />
    );

    const canRemoveAdminship = affiliation && isAdmin(affiliation) && !isParentOrgAdmin;

    return (
      <Buttons align="right">
        {canRemoveAdminship && (
          <ModalTrigger
            className="SettingsSchoolsListItem-removeAdminButton"
            modal={<AdminResignModal affiliation={affiliation} school={school} />}>
            <Button color="apple" disabled={this.props.submitting} level="secondary" size="xsmall">
              Remove me as admin
            </Button>
          </ModalTrigger>
        )}
        {this.renderLeaveAction(_cannotLeaveOrgReasons, removeSchoolModal, this.props.submitting)}
      </Buttons>
    );
  }

  renderGroupAvatars() {
    const { groups } = this.props;
    const avatars = groups
      .slice(0, AVATAR_NUMBER)
      .map(g => (
        <GroupAvatar className="SettingsSchoolsListItem-group" key={g.id} group={g} size="small" />
      ));
    return (
      <div>
        {avatars}
        {groups.length > AVATAR_NUMBER && (
          <Coin
            className="SettingsSchoolsListItem-group"
            color="whiteout"
            border="mascot"
            size="xlarge">
            +{groups.length - AVATAR_NUMBER}
          </Coin>
        )}
      </div>
    );
  }

  renderIcon(school: *) {
    const { affiliation } = this.props;
    const userIsAdmin = affiliation && isAdmin(affiliation);
    const image = <Image height={32} width={32} src={AvatarImages.ORG_AVATAR} />;
    const badge = (
      <Coin
        className="SettingsSchoolsListItem-icon-badge"
        color={userIsAdmin ? 'ballpoint' : 'atlas'}
        size="small">
        <Glyph name={userIsAdmin ? 'crown' : 'check'} color="whiteout" solo />
      </Coin>
    );

    if (isEmpty(school.admins)) {
      return image;
    }

    return (
      <div>
        {image}
        {badge}
      </div>
    );
  }

  renderByline() {
    const { affiliation } = this.props;
    if (!affiliation) {
      return null;
    }

    const roleString = this.state.roles.map(e => roleNameSingular[e]).join(', ');

    if (
      !affiliation.can_have_role_changed ||
      this.state.roles.length === 0 ||
      this.state.savingRole
    ) {
      return (
        <div>
          <span className="SettingsSchoolsListItem-roleString">{roleString}</span>
          {this.state.savingRole && (
            <CircleSpinner
              className="SettingsSchoolsListItem-roleSpinner"
              color="atlas"
              size="small"
            />
          )}
        </div>
      );
    }

    const onClose = roles => {
      if (xor(roles, this.state.roles).length > 0) {
        this.setState({ savingRole: true, roles });
      }
      this.setState({ open: false });
    };

    const roleDropdown = (
      <OrgRoleDropdownMenu
        isCurrentUser
        organizationId={this.props.school.id}
        roles={this.state.roles}
        potentialRoles={affiliation.potential_roles}
        userId={CurrentUser.getUserId()}
        onClose={onClose}
        onFinishSave={() => this.setState({ savingRole: false })}
      />
    );

    return (
      <LayerTrigger layer={roleDropdown} trigger="click" placement="bottom" alignment="left">
        <span
          className="SettingsSchoolsListItem-editableRole"
          onClick={() => this.setState({ open: !this.state.open })}>
          <span className="SettingsSchoolsListItem-editableRole-text">{roleString}</span>
          <Glyph
            name="chevron-down"
            color="graphite"
            size="xxsmall"
            className={cx({
              'SettingsSchoolsListItem-editableRole-chevron': true,
              'SettingsSchoolsListItem-editableRole-chevronClosed': !this.state.open,
              'SettingsSchoolsListItem-editableRole-chevronOpen': this.state.open,
            })}
          />
        </span>
      </LayerTrigger>
    );
  }

  render() {
    const { school, submitting, data } = this.props;

    if (data.loading) {
      return <Ghost />;
    }

    return (
      <div
        className="SettingsSchoolsListItem"
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}>
        <MediaObject center figure="right">
          <MediaObject center gutter="xsmall">
            <div className="SettingsSchoolsListItem-icon">{this.renderIcon(school)}</div>
            <Text color="graphite" size="small" className="SettingsSchoolsListItem-content">
              <Text
                color={submitting ? 'inherit' : 'chalkboard'}
                truncate
                className="SettingsSchoolsListItem-name">
                {school.name}
              </Text>
              {this.renderByline()}
            </Text>
          </MediaObject>
          <div className="SettingsSchoolsListItem-avatarsActions">
            {this.state.hovered ? this.renderHoverContent() : this.renderGroupAvatars()}
          </div>
        </MediaObject>
      </div>
    );
  }
}

const withData = graphql(SETTINGS_SCHOOLS_LIST_ITEM_QUERY, {
  options: ({ school }) => ({
    variables: {
      organizationUuid: school.uuid,
    },
  }),
});

export { SettingsSchoolsListItem };

export default withData(SettingsSchoolsListItem);
